/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Scoped sharing (AGL-1037): org-owned resources — datasets, media assets,
 * media folders — carry a `visibleTo` array naming who may see them, so an
 * agency can keep an internal dataset off its client sites without forking
 * storage into a second host-scoped collection (see AGL-237, which moved
 * these the other way).
 *
 * The shape is dictated by what both enforcement layers can evaluate:
 * Firestore rules have no `.map()`, so the caller's read set is denormalized
 * onto their member doc (AGL-1038) and matched with `list.hasAny(list)`;
 * clients match the same field with `array-contains-any`. A flat array of
 * strings is the only shape both primitives accept — the friendlier
 * `{ kind, hostIds }` form is a presentation concern and lives in
 * `describeScope` below, never on the doc.
 */

import type { ScopeToken } from '../foundation'

// The type itself lives in foundation (app-utils imports foundation, never
// the reverse) so the media doc interfaces can reference it; re-exported
// here so callers get the type and its helpers from one place.
export type { ScopeToken }

/** Everyone in the owning org, and every host it owns. */
export const ORG_SCOPE_TOKEN = 'org' as const satisfies ScopeToken

const HOST_TOKEN_PREFIX = 'host:'

/**
 * `array-contains-any` and `hasAny` both cap at 30 values. That caps a
 * selected-sites scope at 30 hosts, and a collaborator's read query at 29
 * hosts plus `'org'`. Wider than that is the `['org']` case.
 */
export const MAX_SCOPE_HOSTS = 30

/** `host:{hostId}` — the token naming a single site. */
export function hostScopeToken(hostId: string): ScopeToken {
  return `${HOST_TOKEN_PREFIX}${hostId}`
}

export function isScopeToken(value: unknown): value is ScopeToken {
  if (typeof value !== 'string') return false
  if (value === ORG_SCOPE_TOKEN) return true
  return (
    value.startsWith(HOST_TOKEN_PREFIX) &&
    value.length > HOST_TOKEN_PREFIX.length
  )
}

/** Reads a token back apart; null when it isn't one. */
export function parseScopeToken(
  token: string
): { kind: 'org' } | { kind: 'host'; hostId: string } | null {
  if (token === ORG_SCOPE_TOKEN) return { kind: 'org' }
  if (!isScopeToken(token)) return null
  return { kind: 'host', hostId: token.slice(HOST_TOKEN_PREFIX.length) }
}

/** Every host id named by a scope, in order; `['org']` names none. */
export function hostIdsFromScope(
  visibleTo: readonly string[] | undefined
): string[] {
  const ids: string[] = []
  for (const token of visibleTo ?? []) {
    const parsed = parseScopeToken(token)
    if (parsed?.kind === 'host') ids.push(parsed.hostId)
  }
  return ids
}

/**
 * The read set for one site: what a server read path or a scoped client
 * query passes to `array-contains-any`. `'org'` is included because an
 * org-wide resource is visible to every host in the org.
 */
export function scopeTokensForHost(hostId: string): ScopeToken[] {
  return [ORG_SCOPE_TOKEN, hostScopeToken(hostId)]
}

/**
 * Whether a host may see a resource.
 *
 * ## A missing `visibleTo` is visible to NOBODY
 *
 * It once read as org-wide, so that documents predating the scope backfill
 * stayed visible until it reached them. That default is the shape of failure
 * this codebase keeps paying for: **a read that cannot see a value answered
 * the same as a value that is absent.** "This document was never scoped" and
 * "this document is shared with every site" are different facts, and one
 * unscoped document under an agency account was one client's data on every
 * other client's site — silently, because the permissive answer is the one
 * nothing complains about.
 *
 * Both enforcement layers below this function already fail CLOSED on the
 * missing field: `array-contains-any` matches nothing on a document that
 * lacks the array, and the rules' `hasAny` is false for the same reason. So
 * the permissive default was never a policy either of them agreed with — it
 * was one TypeScript helper disagreeing with the database, which is what made
 * AGL-1466 invisible for three weeks.
 *
 * The migration is `tools/scripts/backfill-consent-host.mjs`, which stamps
 * `['org']` on the documents that should carry it. Until a document is
 * stamped it is hidden, and hiding is the recoverable direction.
 *
 * An **empty array** answers the same and always did.
 */
export function visibleToHost(
  visibleTo: readonly string[] | undefined | null,
  hostId: string
): boolean {
  if (visibleTo == null) return false
  if (visibleTo.includes(ORG_SCOPE_TOKEN)) return true
  return visibleTo.includes(hostScopeToken(hostId))
}

/**
 * Whether a caller holding `scopeTokens` (AGL-1038) may see a resource.
 * The TypeScript twin of the rules' `visibleTo.hasAny(scopeTokens)`, for
 * the Admin-SDK paths that never evaluate rules at all.
 *
 * A missing `visibleTo` is visible to nobody, for the reason
 * {@link visibleToHost} gives — and here the twinning is the argument on its
 * own: `hasAny` over a missing field is false in the rules, so a helper that
 * answered `true` was not a twin of anything.
 */
export function visibleToTokens(
  visibleTo: readonly string[] | undefined | null,
  scopeTokens: readonly string[] | undefined | null
): boolean {
  if (visibleTo == null) return false
  if (!scopeTokens?.length) return false
  return visibleTo.some((token) => scopeTokens.includes(token))
}

/**
 * The scope a document ACTUALLY carries — `null` when it carries none
 * (AGL-1466/AGL-1480).
 *
 * Every helper above this line answers "may this be seen", and answers it
 * the way both enforcement layers do: a missing `visibleTo` is seen by
 * nobody.
 *
 * An **editor** asks a different question — "what did somebody choose" —
 * and it still needs its own answer, because "nobody chose" and "somebody
 * chose nobody" are the same to an editor and different on the document.
 * Four call sites across two files each wrote their own
 *
 * ```ts
 * Array.isArray(doc.visibleTo) ? doc.visibleTo : [ORG_SCOPE_TOKEN]
 * ```
 *
 * and every one of them rendered "All sites" over a document no site could
 * see. That inversion is why AGL-1466 went three weeks unnoticed with the
 * folder tree missing from every host, and AGL-1480 is the third and fourth
 * copies of it turning up at the surface people use most.
 *
 * So: one function, the same lesson `newMediaFolderDoc` drew on the write
 * side. A caller that wants a display default may still `?? []` or
 * `?? [ORG_SCOPE_TOKEN]` — but it has to say so, in one visible place,
 * instead of a ternary that reads like a null check.
 *
 * An empty array answers `null` too. On the document the two differ, and
 * `newResourceScopeFields` refuses to WRITE one; to an editor they are the
 * same fact — nobody chose this — and both must offer the choice rather
 * than pre-fill an answer.
 *
 * Not normalized, deliberately. `normalizeVisibleTo` collapses for STORAGE;
 * doing it here would make an untouched editor differ from the document it
 * was seeded from, and the save gates compare exactly those two to decide
 * whether to write at all.
 */
export function storedScope(
  visibleTo: readonly string[] | undefined | null
): string[] | null {
  return Array.isArray(visibleTo) && visibleTo.length ? [...visibleTo] : null
}

/**
 * Cleans a scope for storage: drops junk, dedupes, and collapses to
 * `['org']` when org-wide is present, since that already implies every
 * host. Returns null when the result is unusable — empty, or over
 * `MAX_SCOPE_HOSTS` — so the caller decides rather than this silently
 * dropping hosts (losing access) or widening to org (leaking).
 */
export function normalizeVisibleTo(
  input: readonly string[] | undefined | null
): ScopeToken[] | null {
  const tokens = (input ?? []).filter(isScopeToken)
  if (!tokens.length) return null
  if (tokens.includes(ORG_SCOPE_TOKEN)) return [ORG_SCOPE_TOKEN]
  const unique = [...new Set(tokens)]
  if (unique.length > MAX_SCOPE_HOSTS) return null
  return unique
}

/**
 * The scope a save writes, or why the selection cannot be written.
 *
 * {@link normalizeVisibleTo} answers null for a selection it cannot store, and
 * a save has no safe substitute for that null: the org token shares the
 * resource with every site, and dropping tokens takes access from sites
 * somebody picked. So a caller refuses the save and shows `problem`.
 */
export function scopeToStore(
  input: readonly string[] | undefined | null
): { scope: ScopeToken[]; problem: null } | { scope: null; problem: string } {
  const scope = normalizeVisibleTo(input)
  if (scope) return { scope, problem: null }
  return {
    scope: null,
    problem: (input ?? []).some(isScopeToken)
      ? `Choose ${MAX_SCOPE_HOSTS} sites or fewer, or share with All sites.`
      : 'Choose at least one site, or share with All sites.',
  }
}

/** A scope covering exactly the given hosts; null when unusable. */
export function scopeForHosts(
  hostIds: readonly string[]
): ScopeToken[] | null {
  return normalizeVisibleTo(hostIds.map(hostScopeToken))
}

/**
 * Whether a scope is the org-wide one.
 *
 * An absent scope is NOT org-wide — see {@link visibleToHost}. That is the
 * whole correction: this predicate is what `describeScope` renders and what
 * `scopeCovers` and `narrowsScope` reason from, and while it answered `true`
 * for a document nothing could see, the console said "All sites" over it.
 */
export function isOrgWideScope(
  visibleTo: readonly string[] | undefined | null
): boolean {
  return visibleTo != null && visibleTo.includes(ORG_SCOPE_TOKEN)
}

/**
 * Whether moving from `before` to `after` takes visibility away from any
 * host — the direction that can break a published page, and the one the
 * console confirms before saving (AGL-1044/AGL-1045).
 */
export function narrowsScope(
  before: readonly string[] | undefined | null,
  after: readonly string[] | undefined | null
): boolean {
  if (isOrgWideScope(before)) return !isOrgWideScope(after)
  if (isOrgWideScope(after)) return false
  const kept = new Set(after ?? [])
  return (before ?? []).some((token) => !kept.has(token))
}

/**
 * The sentence the console shows. `hostNames` maps host id → display name;
 * ids missing from it fall back to the count wording, since a name we
 * can't resolve is worse than no name.
 */
export function describeScope(
  visibleTo: readonly string[] | undefined | null,
  hostNames: Readonly<Record<string, string>> = {}
): string {
  if (isOrgWideScope(visibleTo)) return 'All sites'
  const hostIds = hostIdsFromScope(visibleTo ?? [])
  if (!hostIds.length) return 'No sites'
  if (hostIds.length === 1) {
    const name = hostNames[hostIds[0]]
    return name ? `${name} only` : '1 site'
  }
  return `${hostIds.length} sites`
}

/**
 * The scope a newly created resource starts with (AGL-1048).
 *
 * Honours the org's default — `defaultResourceScope` for a dataset,
 * `defaultMediaScopeOf(org)` for media, passed in the same option — but
 * only when a site is
 * actually in context: created from the org Media or Data page there is no
 * host to scope to, and inventing one would hide the resource from the
 * page that just created it.
 */
export function defaultScopeForNewResource(options: {
  defaultResourceScope?: 'org' | 'host'
  hostId?: string | null
}): ScopeToken[] {
  return options.defaultResourceScope === 'host' && options.hostId
    ? [hostScopeToken(options.hostId)]
    : [ORG_SCOPE_TOKEN]
}

/**
 * The org's default sharing for new MEDIA — uploads and folders (AGL-3662).
 *
 * `defaultMediaScope` when the org has set it, otherwise
 * `defaultResourceScope`: until the two were split one field decided both,
 * so an org that chose "only the site they were created in" before the split
 * keeps that choice for media until it sets media on its own. Datasets read
 * `defaultResourceScope` directly.
 */
export function defaultMediaScopeOf(
  org:
    | {
        defaultMediaScope?: 'org' | 'host'
        defaultResourceScope?: 'org' | 'host'
      }
    | null
    | undefined,
): 'org' | 'host' | undefined {
  return org?.defaultMediaScope ?? org?.defaultResourceScope
}

/**
 * The scope fields a document being CREATED in a scoped collection carries
 * (AGL-1478), as a spreadable object.
 *
 * The argument is REQUIRED and has no default, which is the whole point.
 * AGL-1466 was two call sites that each built a `mediaFolders` document
 * inline and each forgot `visibleTo`; `newMediaFolderDoc` fixed that one
 * collection by making the input mandatory, and this is the same guarantee
 * for the collections that have no document constructor of their own —
 * `datasets`, `media`, `contacts`, `contactSegments`. A creator that has
 * not decided cannot compile.
 *
 * Three inputs, three meanings:
 *
 * - **tokens** — stamp them.
 * - **`null`** — this collection is NOT scoped (a `hosts/{hostId}`
 *   subcollection is private by construction), so store nothing. Saying it
 *   out loud is what distinguishes "not applicable" from "forgotten".
 * - **`[]`** — throws. Unlike a missing field, a stored empty array is a
 *   written "visible to nobody": the backfill leaves it alone by design and
 *   no read path treats it as legacy, so it is permanent where absent is
 *   repairable. Getting here means a caller computed a scope and came back
 *   with nothing, which is worth a stack trace.
 */
export function newResourceScopeFields(
  visibleTo: readonly ScopeToken[] | null
): { visibleTo?: ScopeToken[] } {
  if (visibleTo && !visibleTo.length) {
    throw new Error('A scoped resource cannot be created with an empty scope')
  }
  return visibleTo ? { visibleTo: [...visibleTo] } : {}
}

/**
 * Whether `target` is visible everywhere `source` is — the condition a
 * cross-dataset REFERENCE has to satisfy (AGL-1044).
 *
 * A reference field on dataset A pointing at dataset B is only resolvable
 * on the sites that can see BOTH. If A is shared with a site that cannot
 * see B, a page there resolves the reference to nothing: the row renders
 * with a blank where the linked value should be, and nothing says why. So
 * B's scope must COVER A's.
 *
 * Org-wide covers everything. A missing scope is seen by nobody and
 * therefore covers nothing, matching the rest of this module.
 */
export function scopeCovers(
  target: readonly string[] | undefined | null,
  source: readonly string[] | undefined | null,
): boolean {
  if (isOrgWideScope(target)) return true
  // The target is restricted; an org-wide source reaches sites it cannot.
  if (isOrgWideScope(source)) return false
  const reach = new Set(target ?? [])
  return (source ?? []).every((token) => reach.has(token))
}

/**
 * Rewrites an org media CDN path so it names the site using the asset
 * (AGL-1043/1045).
 *
 * `/api/media/cdn/org:{orgId}/{mediaId}` serves ORG-WIDE assets only. A
 * restricted asset has to be requested through the form that names the
 * site — `org:{orgId}:{hostId}` — because the CDN is unauthenticated and
 * the URL is the only thing it can decide from.
 *
 * Returns the path untouched when there is nothing to do: an org-wide
 * asset, a host-library asset, no host context, or a path that is not an
 * org CDN URL. That keeps it safe to apply blindly at every pick site.
 */
export function hostQualifiedCdnPath(
  cdnPath: string | undefined | null,
  visibleTo: readonly string[] | undefined | null,
  hostId: string | undefined | null,
): string | undefined {
  if (!cdnPath) return cdnPath ?? undefined
  if (!hostId || isOrgWideScope(visibleTo)) return cdnPath
  // `/api/media/cdn/{scope}/{mediaId}[/{hash}]`
  const marker = '/api/media/cdn/'
  const at = cdnPath.indexOf(marker)
  if (at === -1) return cdnPath
  const head = cdnPath.slice(0, at + marker.length)
  const rest = cdnPath.slice(at + marker.length)
  const [scopeSegment, ...tail] = rest.split('/')
  // Only the bare `org:{orgId}` form is rewritten; an already-qualified
  // scope is left alone rather than gaining a second host.
  if (!scopeSegment.startsWith('org:') || scopeSegment.includes(':', 4)) {
    return cdnPath
  }
  return [`${head}${scopeSegment}:${hostId}`, ...tail].join('/')
}

/*==========================================
 * SEEN, AND HELD (AGL-3336).
 *
 * `visibleTo` answers one question, "who may see this", because it is the
 * one field both enforcement layers can evaluate. A visibility GRANT — an
 * org manager sharing a record with another site, by hand or by a rule —
 * has to widen exactly that field, or the other site's lists, which are
 * real `array-contains-any` queries, could never return the record.
 *
 * But a site a record was shared with does not HOLD it. It did not capture
 * the person, it holds no consent from them, and letting go of the record
 * is not its decision. So a document that carries a grant records which of
 * its tokens the grant added, under `sharing.added`, and every question
 * about holding — who may delete it, whose audience it joins, which site's
 * list it enters — reads {@link heldScopeTokens} instead of `visibleTo`.
 *
 * A document without the field is held by everything that sees it, which
 * is every document written before grants existed.
 *=========================================*/

/** The map a scoped document records its visibility grants under. */
export const SCOPE_GRANTS_FIELD = 'sharing'

/** The tokens a grant ADDED to `visibleTo`: seen through, never held. */
export function grantedScopeTokens(
  doc: Readonly<Record<string, unknown>> | null | undefined,
): string[] {
  const grants = (doc ?? {})[SCOPE_GRANTS_FIELD]
  if (!grants || typeof grants !== 'object' || Array.isArray(grants)) return []
  const added = (grants as Record<string, unknown>)['added']
  return Array.isArray(added)
    ? added.filter((token): token is string => typeof token === 'string')
    : []
}

/**
 * The tokens that HOLD a document: `visibleTo` without the ones a grant
 * added. Missing `visibleTo` is held by nobody, as it is seen by nobody.
 *
 * A site that created the document (`hostId`) or captured the person on it
 * (`capturedByHostIds`) holds its token whatever the grants say: a record
 * shared with a site that then captures the person itself is that site's
 * own from the capture on, before anything re-evaluates the grants.
 */
export function heldScopeTokens(
  doc: Readonly<Record<string, unknown>> | null | undefined,
): string[] {
  const visibleTo = (doc ?? {})['visibleTo']
  if (!Array.isArray(visibleTo)) return []
  const granted = new Set(grantedScopeTokens(doc))
  const capturing = new Set<string>()
  const hostId = (doc ?? {})['hostId']
  if (typeof hostId === 'string' && hostId) capturing.add(hostScopeToken(hostId))
  const captured = (doc ?? {})['capturedByHostIds']
  if (Array.isArray(captured)) {
    for (const id of captured) if (typeof id === 'string' && id) capturing.add(hostScopeToken(id))
  }
  return visibleTo.filter(
    (token): token is string =>
      typeof token === 'string' && (!granted.has(token) || capturing.has(token)),
  )
}

/**
 * Whether a site HOLDS a document rather than only seeing it through a
 * grant. The question a marketing audience, a list rule, a sequence and a
 * holder count ask; {@link visibleToHost} is the one a reader asks.
 */
export function heldByHost(
  doc: Readonly<Record<string, unknown>> | null | undefined,
  hostId: string,
): boolean {
  return visibleToHost(heldScopeTokens(doc), hostId)
}

/**
 * Whether a site sees a document ONLY through a grant: it carries grants and
 * the site does not hold it. The filter an audience sweep applies to what
 * its scoped query returned — a document with no grants answers `false`, so
 * every read that predates sharing keeps exactly the rows it had.
 */
export function seenOnlyThroughGrant(
  doc: Readonly<Record<string, unknown>> | null | undefined,
  hostId: string,
): boolean {
  return grantedScopeTokens(doc).length > 0 && !heldByHost(doc, hostId)
}
